import { RestError } from '@azure/core-http';
import { Container } from '@mui/material';
import { useMemo } from 'react';
import { Redirect } from 'react-router-dom';
import { createPath, Paths, ProjectPathParams } from '../../../app/router/app.router';
import { isCarrierProjectType } from '../../../app/navigation/courier-project-type';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { CouriersTable } from './components/couriers-list-table.component';
import { useCouriersList } from './hooks/use-couriers-list.hook';
import { makeCouriersListPageHeaderProps } from './logic/couriers-list-props.map';
import { CouriersListActionsObserver } from './logic/courier-list-actions-observer.component';

export const CouriersListPage = ({ params }: { params: ProjectPathParams<Paths.couriers> }) => {
  const {
    project,
    result: couriers,
    infiniteScroll,
    formData,
    setFormData,
    carrierOptions,
    actions
  } = useCouriersList(params.projectID);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );
  const error = project.error || couriers.error;

  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  // The couriers list only applies to carrier/region projects — if the user switches to a
  // project of a different type while on this page, send them to that project's default page.
  if (project.data && !isCarrierProjectType(project.data.projectType)) {
    return <Redirect to={createPath(Paths.dashboard, { projectID: params.shortcode || params.projectID })} />;
  }

  const headerProps = makeCouriersListPageHeaderProps(
    couriers.data?.pages[0].totalRecords || 0,
    formData,
    setFormData,
    carrierOptions,
    actions.setAction,
    params.projectID
  );

  return (
    <Container>
      {project.data && <WindowTitle detail={project.data.name} pageTitle="Couriers" />}
      <ListPageHeader {...headerProps} />
      <CouriersTable
        params={params}
        pages={couriers.data?.pages || []}
        infiniteScroll={infiniteScroll}
        setFormData={setFormData}
        sortData={sortData}
        isLoading={couriers.isLoading}
        isPreviousData={couriers.isPreviousData}
        hasError={couriers.isError}
      />
      <CouriersListActionsObserver actions={actions} projectID={params.projectID} />
    </Container>
  );
};
